import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LocationSearch } from "@/components/LocationSearch";
import { PageShell, btnGhost, btnPrimary, cellCls, headCls, inputCls } from "@/components/TopBar";

export default function Geofences() {
  const [fences, setFences] = useState([]);
  const [idleMinutes, setIdleMinutes] = useState(60);
  const [speedLimit, setSpeedLimit] = useState(80);
  const [liveFeed, setLiveFeed] = useState(true);
  const [name, setName] = useState("");
  const [center, setCenter] = useState(null);
  const [radius, setRadius] = useState(1000);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [g, s] = await Promise.all([api.get("/geofences"), api.get("/settings")]);
    setFences(g.data);
    setIdleMinutes(s.data.idle_alert_minutes);
    setSpeedLimit(s.data.speed_limit_kmh);
    setLiveFeed(s.data.live_feed !== false);
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load geofences"));
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || !center) return toast.error("Name and a searched centre are required");
    setBusy(true);
    try {
      await api.post("/geofences", {
        name: name.trim(),
        center: { lat: center.lat, lng: center.lng },
        radius_m: Number(radius) || 500,
      });
      toast.success("Geofence created");
      setName("");
      setCenter(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/geofences/${id}`, body);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete geofence ${f.name}?`)) return;
    try {
      await api.delete(`/geofences/${f.id}`);
      toast.success("Geofence deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const saveIdle = async () => {
    try {
      await api.patch("/settings", {
        idle_alert_minutes: Number(idleMinutes),
        speed_limit_kmh: Number(speedLimit),
        live_feed: liveFeed,
      });
      toast.success("Alert thresholds saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <PageShell
      testid="geofences-page"
      subtitle="alerts"
      title="GEOFENCES & WATCHDOG"
      actions={
        <button data-testid="new-geofence-btn" onClick={() => setShowForm((s) => !s)} className={btnPrimary}>
          <Plus size={12} /> {showForm ? "CLOSE" : "ADD GEOFENCE"}
        </button>
      }
    >
      <div className="mb-8 flex flex-wrap items-end gap-4 border border-white/15 bg-[#121212] p-5" data-testid="idle-settings">
        <div>
          <label className="overline mb-2 block">idle alert after (minutes)</label>
          <input
            data-testid="idle-minutes-input"
            type="number"
            min="5"
            value={idleMinutes}
            onChange={(e) => setIdleMinutes(e.target.value)}
            className={`${inputCls} w-32`}
          />
        </div>
        <div>
          <label className="overline mb-2 block">speed limit (km/h)</label>
          <input
            data-testid="speed-limit-input"
            type="number"
            min="20"
            value={speedLimit}
            onChange={(e) => setSpeedLimit(e.target.value)}
            className={`${inputCls} w-32`}
          />
        </div>
        <div>
          <label className="overline mb-2 block">live feed</label>
          <button
            data-testid="live-feed-toggle"
            onClick={() => setLiveFeed((v) => !v)}
            className={btnGhost}
            style={liveFeed ? { borderColor: "#00FF6655", color: "#00FF66" } : {}}
          >
            {liveFeed ? "ON - ALL TRUCKS LIVE" : "OFF"}
          </button>
        </div>
        <button data-testid="save-idle-btn" onClick={saveIdle} className={btnGhost}>
          SAVE THRESHOLDS
        </button>
        <p className="overline max-w-sm">
          dispatchers get an alert when a truck stays parked too long, crosses the speed limit, or enters
          or leaves a fence
        </p>
      </div>

      {showForm && (
        <form onSubmit={create} data-testid="geofence-form" className="mb-8 grid gap-5 border border-white/15 bg-[#121212] p-5 sm:grid-cols-3">
          <div>
            <label className="overline mb-2 block">fence name</label>
            <input
              data-testid="geofence-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dimapur Yard"
              className={inputCls}
            />
          </div>
          <LocationSearch label="centre (search & set)" value={center} onChange={setCenter} testid="geofence-center" />
          <div>
            <label className="overline mb-2 block">radius (metres)</label>
            <input
              data-testid="geofence-radius-input"
              type="number"
              min="50"
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              className={inputCls}
            />
            <button type="submit" disabled={busy} data-testid="submit-geofence-btn" className={`${btnPrimary} mt-4 w-full py-2.5`}>
              {busy ? "saving" : "create fence"}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[760px]" data-testid="geofences-table">
          <thead className="border-b border-white/10 bg-[#121212]">
            <tr>
              <th className={headCls}>name</th>
              <th className={headCls}>centre</th>
              <th className={headCls}>radius</th>
              <th className={headCls}>on enter</th>
              <th className={headCls}>on exit</th>
              <th className={headCls}>actions</th>
            </tr>
          </thead>
          <tbody>
            {fences.length === 0 && (
              <tr>
                <td className={`${cellCls} text-[#52525B]`} colSpan={6} data-testid="geofences-empty">
                  NO GEOFENCES YET
                </td>
              </tr>
            )}
            {fences.map((f) => (
              <tr key={f.id} data-testid={`geofence-row-${f.id}`} className="border-b border-white/5 hover:bg-[#141414]">
                <td className={`${cellCls} font-head font-bold`}>{f.name}</td>
                <td className={`${cellCls} text-[#A1A1AA]`}>
                  {f.center.lat.toFixed(4)}, {f.center.lng.toFixed(4)}
                </td>
                <td className={cellCls}>{f.radius_m} m</td>
                <td className={cellCls}>
                  <button
                    data-testid={`toggle-enter-${f.id}`}
                    onClick={() => patch(f.id, { alert_on_enter: !f.alert_on_enter })}
                    className={btnGhost}
                  >
                    {f.alert_on_enter ? "ON" : "OFF"}
                  </button>
                </td>
                <td className={cellCls}>
                  <button
                    data-testid={`toggle-exit-${f.id}`}
                    onClick={() => patch(f.id, { alert_on_exit: !f.alert_on_exit })}
                    className={btnGhost}
                  >
                    {f.alert_on_exit ? "ON" : "OFF"}
                  </button>
                </td>
                <td className={cellCls}>
                  <button
                    data-testid={`delete-geofence-btn-${f.id}`}
                    onClick={() => remove(f)}
                    className="border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                    aria-label="Delete geofence"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
